import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { http } from "../../lib/auth";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const STATUSES = ["new", "contacted", "booked", "not a fit"];
const Th = ({ children }) => <th className="whitespace-nowrap px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[0.2em] text-neutral-500">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-4 align-top text-sm text-neutral-800 ${className}`}>{children}</td>;

const useList = (url) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => http.get(url).then(({ data }) => setRows(data)).catch(() => toast.error("Couldn't load data.")), [url]);
  useEffect(() => { load(); }, [load]);
  return [rows, load];
};

const Shell = ({ rows, empty, children, testid }) => (
  <div className="overflow-x-auto bg-white" data-testid={testid}>
    {rows === null ? <p className="p-10 kicker">Loading…</p> : rows.length === 0 ? <p className="p-10 text-sm font-light text-neutral-500">{empty}</p> : children}
  </div>
);

export const SubscribersTab = () => {
  const [rows] = useList("/admin/subscribers");
  return (
    <Shell rows={rows} empty="No skin tips sign-ups yet." testid="subscribers-table">
      <table className="w-full">
        <thead className="border-b border-neutral-200"><tr><Th>Email</Th><Th>Signed up from</Th><Th>Date</Th></tr></thead>
        <tbody>
          {rows?.map((r) => (
            <tr key={r.email} className="border-b border-neutral-100"><Td>{r.email}</Td><Td>{r.source ? <a href={`/blog/${r.source}`} className="underline">{r.source}</a> : "—"}</Td><Td className="whitespace-nowrap">{fmt(r.created_at)}</Td></tr>
          ))}
        </tbody>
      </table>
    </Shell>
  );
};

export const LeadsTab = () => {
  const [rows, load] = useList("/admin/leads");
  const setStatus = async (id, status) => {
    await http.patch(`/admin/leads/${id}`, { status });
    toast.success("Status updated");
    load();
  };
  return (
    <Shell rows={rows} empty="No leads yet. They'll appear here as soon as someone submits a landing page form." testid="admin-leads-table">
      <p className="px-4 pt-5 text-xs text-neutral-500">Sorted by budget, highest first.</p>
      <table className="w-full">
        <thead className="border-b border-neutral-200"><tr><Th>Budget</Th><Th>Name</Th><Th>Contact</Th><Th>Treatment</Th><Th>Page</Th><Th>Ad / Campaign</Th><Th>Received</Th><Th>Status</Th></tr></thead>
        <tbody className="divide-y divide-neutral-100">
          {rows?.map((r) => (
            <tr key={r.id} data-testid={`lead-row-${r.id}`}>
              <Td className="font-medium whitespace-nowrap">{r.budget}</Td>
              <Td>{r.name}</Td>
              <Td><a href={`mailto:${r.email}`} className="underline">{r.email}</a>{r.phone && <><br /><a href={`tel:${r.phone}`}>{r.phone}</a></>}</Td>
              <Td>{r.treatment}{(r.package_interest || r.membership_interest) && <span className="mt-1 block text-xs text-neutral-500">Package: {r.package_interest || "—"} · Membership: {r.membership_interest || "—"}</span>}</Td>
              <Td className="whitespace-nowrap">{r.page || "—"}</Td>
              <Td>{r.utm_content || "—"}<br /><span className="text-xs text-neutral-500">{r.utm_campaign || r.utm_source || r.source}</span></Td>
              <Td className="whitespace-nowrap">{fmt(r.created_at)}</Td>
              <Td>
                <Select value={r.status || "new"} onValueChange={(v) => setStatus(r.id, v)}>
                  <SelectTrigger className="h-9 w-[130px] rounded-none text-xs capitalize" data-testid={`lead-status-${r.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Shell>
  );
};

export const ConsultationsTab = () => {
  const [rows] = useList("/admin/consultations");
  return (
    <Shell rows={rows} empty="No consultation requests yet." testid="admin-consultations-table">
      <table className="w-full">
        <thead className="border-b border-neutral-200"><tr><Th>Name</Th><Th>Contact</Th><Th>Treatment</Th><Th>Preferred Date</Th><Th>Reach via</Th><Th>Goals</Th><Th>Received</Th></tr></thead>
        <tbody className="divide-y divide-neutral-100">
          {rows?.map((r) => (
            <tr key={r.id}>
              <Td>{r.name}</Td>
              <Td><a href={`mailto:${r.email}`} className="underline">{r.email}</a><br /><a href={`tel:${r.phone}`}>{r.phone}</a></Td>
              <Td>{r.treatment}</Td>
              <Td className="whitespace-nowrap">{r.preferred_date || "—"}</Td>
              <Td>{r.contact_method}</Td>
              <Td className="max-w-xs">{r.message || "—"}</Td>
              <Td className="whitespace-nowrap">{fmt(r.created_at)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Shell>
  );
};

export const AdStatsTab = () => {
  const [rows] = useList("/admin/ad-stats");
  return (
    <div className="space-y-6">
      <div className="bg-white p-6 text-sm font-light leading-relaxed text-neutral-700" data-testid="ad-tracking-help">
        <p className="font-medium text-neutral-950">How to tag your Facebook ads</p>
        <p className="mt-2">In Ads Manager → your ad → <em>URL parameters</em>, paste:</p>
        <code className="mt-2 block break-all bg-neutral-100 p-3 text-xs">utm_source=facebook&amp;utm_campaign={"{{campaign.name}}"}&amp;utm_content={"{{ad.name}}"}</code>
        <p className="mt-2">Point ads to <strong>/glow</strong> (quick lead form), <strong>/new-client-offer</strong> or <strong>/private-consultation</strong>. Leads also fire the Meta Pixel “Lead” event.</p>
        <p className="mt-4 font-medium text-neutral-950">Your Facebook lead page</p>
        <code className="mt-2 block break-all bg-neutral-100 p-3 text-xs" data-testid="fb-landing-url">{`${window.location.origin}/glow`}</code>
        <p className="mt-2">Every submission is emailed to you instantly and appears in the Leads tab.</p>
        <a href="/glow" target="_blank" rel="noreferrer" className="btn-ink mt-4 px-6 py-3" data-testid="open-fb-landing-btn">Open Facebook Lead Page</a>
      </div>
      <Shell rows={rows} empty="No ad leads yet." testid="admin-ad-stats-table">
        <table className="w-full">
          <thead className="border-b border-neutral-200"><tr><Th>Source</Th><Th>Campaign</Th><Th>Ad</Th><Th>Leads</Th><Th>$1,500+ Budget</Th><Th>Booked</Th><Th>Latest</Th></tr></thead>
          <tbody className="divide-y divide-neutral-100">
            {rows?.map((r, i) => (
              <tr key={i}>
                <Td>{r.source}</Td><Td>{r.campaign}</Td><Td>{r.ad}</Td>
                <Td className="text-lg font-medium">{r.leads}</Td><Td>{r.high_budget}</Td><Td>{r.booked}</Td>
                <Td className="whitespace-nowrap">{fmt(r.last)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Shell>
    </div>
  );
};
